import React from 'react';
import PropTypes from 'prop-types';

import { T, SPLIT_WIDTH } from '../constants/tokens';

/**
 * Places the detail panel next to the content or over it.
 *
 * At SPLIT_WIDTH and above the panel is a static column in the screen's grid.
 * Below it the design turns the panel into a fixed right-hand drawer with a
 * scrim — `asidePos: wide ? "static" : "fixed"`, `asideWidth: min(420px,
 * 92vw)` — rather than letting it stack underneath the list, where a selection
 * made at the top of a long list would open a panel far below the fold.
 *
 * Callers render `<DetailAside>` as the second child of a two-column grid and
 * this decides what that means at the current width.
 */
export default function DetailAside({ windowWidth, open, onClose, children }) {
  const wide = windowWidth >= SPLIT_WIDTH;

  if (wide) {
    return <div style={{ minWidth: 0, alignSelf: 'start' }}>{children}</div>;
  }

  if (!open) return null;

  return (
    <>
      <div
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, zIndex: 1200, background: T.scrim }}
      />
      <aside
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          zIndex: 1201,
          width: 'min(420px, 92vw)',
          background: T.elevated,
          borderLeft: '1px solid ' + T.hairline,
          boxShadow: '-12px 0 32px rgba(0,0,0,0.18)',
          overflowY: 'auto'
        }}
      >
        {children}
      </aside>
    </>
  );
}

DetailAside.propTypes = {
  windowWidth: PropTypes.number.isRequired,
  /** Whether anything is selected. Ignored at wide widths. */
  open: PropTypes.bool,
  onClose: PropTypes.func,
  children: PropTypes.node
};
